/** Keep in sync with `PDP_MOBILE_MQ` in detail-gallery-core.js. */
const PDP_ZOOM_MQ = '(hover: hover) and (pointer: fine) and (min-width: 993px)';
const ZOOM = 2;
const IN_MS = 280;
const OUT_MS = 180;

let bound = false;
let pointerDown = false;
let zoomedSlide = null;
let armed = false;
let easingIn = false;
let inTimer = 0;
let outTimer = 0;
let rafId = 0;
let lastPoint = null;
let slideRect = null;

function zoomEnabled() {
  return typeof window !== 'undefined' && window.matchMedia(PDP_ZOOM_MQ).matches;
}

function stageScope(stage) {
  return stage.closest('.item_main_info') || document;
}

function isVideoActive(stage) {
  const scope = stageScope(stage);
  if (scope.querySelector('.wrapp_thumbs .video_thumb.current, .wrapp_thumbs .video_thumb.swiper-slide-thumb-active')) {
    return true;
  }

  const overlay = stage.querySelector('.offers_video');
  if (overlay instanceof HTMLElement) {
    const display = overlay.style.display || window.getComputedStyle(overlay).display;
    if (display && display !== 'none') {
      return true;
    }
  }

  const active = stage.querySelector('.item_slider.pdp-gallery .swiper-slide-active');
  return Boolean(active && active.classList.contains('video_thumb_mobile'));
}

function syncVideoState() {
  document.querySelectorAll('.catalog_detail .pdp-gallery-stage').forEach((stage) => {
    if (!(stage instanceof HTMLElement)) return;
    const video = isVideoActive(stage);
    stage.classList.toggle('is-pdp-zoom-video', video);
    if (video && (armed || zoomedSlide)) {
      endZoomNow();
    }
  });
}

function watchVideoState() {
  document.querySelectorAll('.catalog_detail .pdp-gallery-stage').forEach((stage) => {
    if (!(stage instanceof HTMLElement) || stage.dataset.pdpZoomVideoWatch) {
      return;
    }
    stage.dataset.pdpZoomVideoWatch = '1';
    const observer = new MutationObserver(syncVideoState);
    const thumbs = stageScope(stage).querySelector('.wrapp_thumbs');
    if (thumbs) {
      observer.observe(thumbs, { subtree: true, attributes: true, attributeFilter: ['class'] });
    }
    stage.querySelectorAll(':scope > .offers_video').forEach((overlay) => {
      observer.observe(overlay, { attributes: true, attributeFilter: ['style'] });
    });
  });
  syncVideoState();
}

function cacheRect(slide) {
  slideRect = slide.getBoundingClientRect();
}

function applyPan(slide, clientX, clientY) {
  const rect = slideRect;
  if (!rect || !rect.width || !rect.height) return;
  const px = Math.min(rect.width, Math.max(0, clientX - rect.left));
  const py = Math.min(rect.height, Math.max(0, clientY - rect.top));
  slide.style.setProperty('--pdp-zoom-tx', `${px * (1 - ZOOM)}px`);
  slide.style.setProperty('--pdp-zoom-ty', `${py * (1 - ZOOM)}px`);
  slide.style.setProperty('--pdp-zoom-s', String(ZOOM));
}

function clearZoomVars(slide) {
  slide.classList.remove('is-pdp-zoomed', 'is-pdp-zoom-out', 'is-pdp-zoom-in');
  slide.style.removeProperty('--pdp-zoom-tx');
  slide.style.removeProperty('--pdp-zoom-ty');
  slide.style.removeProperty('--pdp-zoom-s');
}

function cancelTimers() {
  window.clearTimeout(inTimer);
  window.clearTimeout(outTimer);
  inTimer = 0;
  outTimer = 0;
}

function endZoomNow() {
  armed = false;
  easingIn = false;
  cancelTimers();
  lastPoint = null;
  slideRect = null;
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = 0;
  }
  if (!(zoomedSlide instanceof HTMLElement)) return;

  const slide = zoomedSlide;
  slide.classList.add('is-pdp-zoom-out');
  slide.style.setProperty('--pdp-zoom-tx', '0px');
  slide.style.setProperty('--pdp-zoom-ty', '0px');
  slide.style.setProperty('--pdp-zoom-s', '1');
  outTimer = window.setTimeout(() => {
    if (zoomedSlide !== slide) return;
    clearZoomVars(slide);
    zoomedSlide = null;
    outTimer = 0;
  }, OUT_MS);
}

function pointInRect(rect, clientX, clientY) {
  return (
    clientX >= rect.left
    && clientX <= rect.right
    && clientY >= rect.top
    && clientY <= rect.bottom
  );
}

function findSlide(clientX, clientY, eventTarget) {
  const stage = document.querySelector('.catalog_detail .pdp-gallery-stage');
  const gallery = stage?.querySelector('.item_slider.pdp-gallery');
  if (!(gallery instanceof HTMLElement)) {
    return null;
  }
  if (eventTarget instanceof Node && !gallery.contains(eventTarget)) {
    return null;
  }
  if (stage instanceof HTMLElement && stage.classList.contains('is-pdp-zoom-video')) {
    return null;
  }

  const galleryRect = gallery.getBoundingClientRect();
  if (!pointInRect(galleryRect, clientX, clientY)) {
    return null;
  }

  const slides = gallery.querySelectorAll('.swiper-slide:not(.video_thumb_mobile)');
  for (let i = 0; i < slides.length; i += 1) {
    const slide = slides[i];
    if (!(slide instanceof HTMLElement) || !slide.querySelector('img')) continue;
    const rect = slide.getBoundingClientRect();
    const visible =
      rect.right > galleryRect.left
      && rect.left < galleryRect.right
      && rect.bottom > galleryRect.top
      && rect.top < galleryRect.bottom;
    if (!visible) continue;
    if (pointInRect(rect, clientX, clientY)) {
      return slide;
    }
  }
  return null;
}

function startZoom(slide, clientX, clientY) {
  cancelTimers();
  if (zoomedSlide instanceof HTMLElement && zoomedSlide !== slide) {
    clearZoomVars(zoomedSlide);
  }

  zoomedSlide = slide;
  slide.classList.remove('is-pdp-zoom-out');
  slide.classList.add('is-pdp-zoomed', 'is-pdp-zoom-in');
  cacheRect(slide);
  slide.style.setProperty('--pdp-zoom-tx', '0px');
  slide.style.setProperty('--pdp-zoom-ty', '0px');
  slide.style.setProperty('--pdp-zoom-s', '1');
  easingIn = true;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (zoomedSlide !== slide || !armed) return;
      applyPan(slide, clientX, clientY);
      inTimer = window.setTimeout(() => {
        if (zoomedSlide !== slide) return;
        slide.classList.remove('is-pdp-zoom-in');
        easingIn = false;
        inTimer = 0;
        if (lastPoint) {
          applyPan(slide, lastPoint.x, lastPoint.y);
        }
      }, IN_MS);
    });
  });
}

function flushPan() {
  rafId = 0;
  const point = lastPoint;
  if (!point || !armed || !(zoomedSlide instanceof HTMLElement)) return;

  if (!slideRect || !pointInRect(slideRect, point.x, point.y)) {
    endZoomNow();
    return;
  }

  if (!easingIn) {
    applyPan(zoomedSlide, point.x, point.y);
  }
}

function armZoom(slide, clientX, clientY) {
  armed = true;
  lastPoint = { x: clientX, y: clientY };
  startZoom(slide, clientX, clientY);
}

function onGalleryClick(event) {
  if (event.pointerType && event.pointerType !== 'mouse') return;
  if (!zoomEnabled()) return;
  const slide = findSlide(event.clientX, event.clientY, event.target);
  if (!slide) return;
  event.preventDefault();
  event.stopPropagation();
  pointerDown = false;
  if (armed) {
    endZoomNow();
    return;
  }
  armZoom(slide, event.clientX, event.clientY);
}

function onPointerMove(event) {
  if (event.pointerType !== 'mouse' || !zoomEnabled()) {
    if (armed || zoomedSlide) {
      endZoomNow();
    }
    return;
  }

  if (!armed || pointerDown) return;

  lastPoint = { x: event.clientX, y: event.clientY };
  if (!rafId) {
    rafId = requestAnimationFrame(flushPan);
  }
}

function bindZoomOnce() {
  if (bound || typeof document === 'undefined') return;
  bound = true;

  document.addEventListener('click', onGalleryClick, true);
  document.addEventListener('pointermove', onPointerMove, { passive: true });
  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse') return;
    pointerDown = true;
    if (findSlide(event.clientX, event.clientY, event.target)) {
      return;
    }
    endZoomNow();
  });
  document.addEventListener('pointerup', () => {
    pointerDown = false;
  });
  document.addEventListener('pointercancel', () => {
    pointerDown = false;
    endZoomNow();
  });
}

export function destroyPdpGalleryZoom() {
  cancelTimers();
  easingIn = false;
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = 0;
  }
  lastPoint = null;
  slideRect = null;
  armed = false;
  if (zoomedSlide instanceof HTMLElement) {
    clearZoomVars(zoomedSlide);
  }
  zoomedSlide = null;
}

export function initPdpGalleryZoom() {
  bindZoomOnce();
  watchVideoState();
}
